import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, ArrowRight, Sparkles } from "lucide-react";
import { PillButton } from "../../components/ui/PillButton";
import { StickerCard } from "../../components/ui/StickerCard";
import { SectionHeading } from "../../components/ui/SectionHeading";
import { CloudsLayer, FloatDeco } from "../../components/ui/Decorations";
import { ActivityCard } from "../../components/ui/ActivityCard";
import { TicketPackageCard } from "../../components/ui/TicketPackageCard";
import { ASSETS } from "../../data/assets";
import { EVENT, ACTIVITIES, HOW_IT_WORKS, EXPERIENCES, FAQS, SPONSORS, TICKET_PACKAGE } from "../../data/mockData";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../../components/ui/accordion";

export default function Home() {
  const navigate = useNavigate();
  const bookable = ACTIVITIES.filter((a) => a.bookingRequired).concat(ACTIVITIES.filter((a) => !a.bookingRequired)).slice(0, 8);

  return (
    <div>
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden pt-8 pb-40 sm:pb-56">
        <CloudsLayer />
        <FloatDeco src={ASSETS.beachBall} className="w-16 sm:w-24 left-[6%] top-[30%] z-10" delay={0.4} />
        <FloatDeco src={ASSETS.swimRing} className="w-16 sm:w-24 right-[7%] top-[22%] z-10" delay={0.8} float="float-slow" />
        <img src={ASSETS.palm2} alt="" className="pointer-events-none absolute -left-6 bottom-24 w-28 sm:w-52 z-10" />
        <img src={ASSETS.palm4} alt="" className="pointer-events-none absolute -right-6 bottom-24 w-28 sm:w-52 z-10" />

        <div className="max-w-5xl mx-auto px-4 text-center relative z-20">
          <motion.img
            src={ASSETS.logo}
            alt="Balita Lili Tata Run 2026"
            className="mx-auto w-56 sm:w-72 drop-shadow-xl"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          />
          <motion.p
            className="mt-3 font-display text-lg sm:text-xl text-sea"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          >
            {EVENT.tagline}
          </motion.p>

          <motion.h1
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-coral sticker-text-sm leading-[1.05]"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          >
            The Ultimate Family<br />Fun Day Starts Here!
          </motion.h1>

          <motion.div
            className="mt-6 flex flex-wrap items-center justify-center gap-3"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          >
            <span className="flex items-center gap-2 bg-white rounded-full px-5 py-2 font-display text-sea-deep shadow-playful">
              <CalendarDays className="w-5 h-5 text-coral" /> {EVENT.dateLabel}
            </span>
            <span className="flex items-center gap-2 bg-white rounded-full px-5 py-2 font-display text-sea-deep shadow-playful">
              <MapPin className="w-5 h-5 text-coral" /> {EVENT.venue}
            </span>
          </motion.div>

          <motion.div
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
          >
            <PillButton size="lg" onClick={() => navigate("/tickets")} data-testid="hero-get-tickets">
              Get Your Tickets
            </PillButton>
            <PillButton size="lg" variant="secondary" onClick={() => navigate("/activities")} data-testid="hero-explore-activities">
              Explore Activities <ArrowRight className="w-5 h-5" />
            </PillButton>
          </motion.div>

          <motion.img
            src={ASSETS.trioHore}
            alt="Baba, Lili & Tata"
            className="mx-auto mt-6 w-full max-w-2xl drop-shadow-2xl relative z-20"
            initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, type: "spring" }}
          />
        </div>

        {/* beach bottom */}
        <img src={ASSETS.sandStrip} alt="" className="pointer-events-none absolute bottom-0 left-0 right-0 w-full h-24 sm:h-32 object-cover z-0" />
        <img src={ASSETS.beachStrip} alt="" className="pointer-events-none absolute bottom-16 sm:bottom-24 left-0 right-0 w-full h-10 object-cover z-0" />
      </section>

      {/* ============ EVENT INTRO ============ */}
      <section className="bg-white py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Tentang Event"
              title="One Ticket. Endless Family Fun."
              subtitle="BLTR 2026 memadukan fun run keluarga dengan beragam aktivitas, hiburan, pengalaman, dan family market — semua dalam satu hari penuh keceriaan!"
            />
            <div className="mt-6 flex flex-wrap gap-2">
              {EXPERIENCES.map((e) => (
                <span key={e.name} className="flex items-center gap-2 bg-sky/15 rounded-full pl-2 pr-4 py-1.5 font-bold text-sm text-sea-deep">
                  <img src={e.icon} alt="" className="w-6 h-6 object-contain" /> {e.name}
                </span>
              ))}
            </div>
            <PillButton className="mt-8" onClick={() => navigate("/tickets")} data-testid="intro-get-tickets">
              Get Your Tickets
            </PillButton>
          </div>
          <div className="relative">
            <StickerCard className="p-4">
              <img src={ASSETS.balapKarung} alt="Keluarga seru bermain" className="w-full rounded-2xl" />
            </StickerCard>
            <FloatDeco src={ASSETS.cloud1} className="w-24 -top-6 -left-6" />
          </div>
        </div>
      </section>

      {/* ============ EXPERIENCE MAP ============ */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <SectionHeading
            eyebrow="Peta Keseruan"
            title="Explore the Fun Map!"
            subtitle="Ikuti jalur petualangan dan temukan semua keseruan yang menanti keluargamu."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {bookable.map((a, i) => (
              <ActivityCard key={a.id} activity={a} index={i} />
            ))}
          </div>
          <div className="text-center mt-10">
            <PillButton size="lg" variant="sunny" onClick={() => navigate("/activities")} data-testid="map-explore-all">
              Explore All Activities <ArrowRight className="w-5 h-5" />
            </PillButton>
          </div>
        </div>
      </section>

      {/* ============ TICKET PACKAGE ============ */}
      <section className="relative bg-sky/25 py-16 sm:py-24 overflow-hidden">
        <CloudsLayer />
        <div className="max-w-6xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center relative z-10">
          <div>
            <SectionHeading
              align="left"
              eyebrow="Tiket"
              title="Parent + Kiddo Package"
              subtitle={`Mulai dari ${TICKET_PACKAGE.priceLabel} ${TICKET_PACKAGE.perLabel}. Satu tiket, dua tiket kebahagiaan!`}
            />
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {TICKET_PACKAGE.benefits.map((b) => (
                <div key={b} className="flex items-center gap-2 bg-white rounded-2xl px-4 py-3 shadow-playful">
                  <Sparkles className="w-4 h-4 text-coral shrink-0" />
                  <span className="text-sm font-bold text-sea-deep">{b}</span>
                </div>
              ))}
            </div>
          </div>
          <TicketPackageCard onBuy={() => navigate("/tickets")} />
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeading eyebrow="Gampang Banget" title="How It Works" subtitle="Hanya 3 langkah menuju hari penuh keceriaan!" />
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((s, i) => (
              <motion.div
                key={s.step}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
              >
                <StickerCard className="p-7 text-center h-full">
                  <div className="mx-auto w-16 h-16 rounded-full bg-coral text-white font-display text-2xl flex items-center justify-center shadow-playful">
                    {s.step}
                  </div>
                  <img src={s.icon} alt="" className="mx-auto mt-4 h-16 object-contain" />
                  <h3 className="mt-3 font-display text-xl text-sea-deep">{s.title}</h3>
                  <p className="mt-1 text-sm font-semibold text-sea-deep/70">{s.desc}</p>
                </StickerCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EVENT INFO ============ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeading eyebrow="Info Event" title="Save the Date!" />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { label: "Tanggal", value: EVENT.dateLabel },
              { label: "Lokasi", value: EVENT.venue },
              { label: "Event", value: EVENT.short },
              { label: "Format", value: EVENT.tagline },
            ].map((info) => (
              <StickerCard key={info.label} className="p-6 text-center">
                <p className="text-xs uppercase tracking-wide font-bold text-coral">{info.label}</p>
                <p className="mt-2 font-display text-lg sm:text-xl text-sea-deep">{info.value}</p>
              </StickerCard>
            ))}
          </div>
          <div className="text-center mt-8">
            <PillButton variant="white" onClick={() => navigate("/event-info")} data-testid="home-event-info">
              Lihat Info Lengkap <ArrowRight className="w-4 h-4" />
            </PillButton>
          </div>
        </div>
      </section>

      {/* ============ SPONSORS ============ */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <SectionHeading title="Sponsors & Partners" subtitle="Terima kasih kepada partner luar biasa yang mendukung BLTR 2026." />
          <div className="mt-10 space-y-8">
            {SPONSORS.map((cat) => (
              <div key={cat.category} className="text-center">
                <p className="text-xs uppercase tracking-widest font-bold text-sea-deep/50">{cat.category}</p>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                  {cat.names.map((n) => (
                    <span key={n} className="bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-3 font-display text-slate-500 text-sm">
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4">
          <SectionHeading eyebrow="Tanya Jawab" title="Frequently Asked Questions" />
          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="bg-white rounded-2xl border-4 border-white shadow-playful px-5"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="font-display text-left text-sea-deep hover:no-underline py-4 text-base">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sea-deep/75 font-semibold pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative overflow-hidden bg-coral py-16 sm:py-24">
        <CloudsLayer className="opacity-40" />
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="font-display text-3xl sm:text-5xl text-white sticker-text-sm">
            Ready for the Ultimate Family Fun Day?
          </h2>
          <p className="mt-4 text-white/90 font-bold text-lg max-w-2xl mx-auto">
            Ikuti Balita Lili Tata Run 2026 untuk sehari penuh lari, tawa, aktivitas, dan momen keluarga tak terlupakan.
          </p>
          <img src={ASSETS.trioHore} alt="" className="mx-auto my-6 w-72 sm:w-96 drop-shadow-2xl" />
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <PillButton size="lg" variant="secondary" onClick={() => navigate("/tickets")} data-testid="final-get-tickets">
              GET YOUR TICKETS
            </PillButton>
            <PillButton size="lg" variant="white" onClick={() => navigate("/activities")} data-testid="final-explore">
              EXPLORE ACTIVITIES
            </PillButton>
          </div>
        </div>
      </section>
    </div>
  );
}
